import React, { useEffect, useRef, useState } from "react";
import {
    Alert, Autocomplete, Box, Breadcrumbs, Button, Chip, CircularProgress, 
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, 
    Divider, Grid, Link, MenuItem, Paper, Select, Snackbar, TextField, 
    Typography, Stack, InputAdornment, FormControl, InputLabel, IconButton, CssBaseline
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Person, School, FamilyRestroom,
    Badge, Cake, AccountBalanceWallet
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import Header from "../../component/header/Header";
import { useNavigate } from "react-router-dom";

// --- Standardized UI Colors ---
const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6",
    inputBg: '#FFFDF0', 
    border: '#F0E6CC'
};

const AddStudentScreen = () => {
    const navigation = useNavigate();
    const formikRef = useRef();

    // --- State Management ---
    const [courseList, setCourseList] = useState([]);
    const [classList, setClassList] = useState([]);
    const [parentList, setParentList] = useState([]);
    const [openSummary, setOpenSummary] = useState(false);
    const [successCreateStudent, setSuccessCreateStudent] = useState(false);
    const [failCreateStudent, setFailCreateStudent] = useState(false);
    const [failErrorMsg, setFailErrorMsg] = useState("");
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        getCourseList();
        getBatchList();
        getParentList();
        localStorage.setItem("current_tab", "Student");
    }, []);

    const getCourseList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/courses`);
            const resJson = await response.json();
            setCourseList(resJson.data || []);
        } catch (error) { console.log(error); }
    };

    const getBatchList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch`);
            const resJson = await response.json();
            setClassList(resJson.data || []);
        } catch (error) { console.log(error); }
    };

    const getParentList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents`);
            const resJson = await response.json();
            setParentList(resJson.data || []);
        } catch (error) { console.log(error); }
    };

    const CreateStudent = async (values) => {
        setLoadingSubmit(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setOpenSummary(false);
                setSuccessCreateStudent(true);
                formikRef.current?.resetForm();
            } else {
                setFailErrorMsg(resJson.message);
                setFailCreateStudent(true);
            }
        } catch (error) {
            setFailCreateStudent(true);
            setFailErrorMsg("Network Error");
        } finally {
            setLoadingSubmit(false);
        }
    };

    const validationSchema = yup.object({
        parentId: yup.string().required("Guardian selection is required"),
        fullname: yup.string().required("Student Name is required"),
        gender: yup.string().required("Gender is required"),
        class: yup.string().required("Batch is required"),
        amount: yup.string().required("Package Amount is required")
    });

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.white, m: 2, borderRadius: 5 }}>
                
                {/* Navigation */}
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Students")} sx={{ cursor: 'pointer' }}>Students</Link>
                    <Typography color="text.primary">Register Student</Typography>
                </Breadcrumbs>

                <Formik
                    innerRef={formikRef}
                    initialValues={{ parentId: "", fullname: "", gender: "", dateOfBirth: "", class: '', amount: "", status: "A", course: [] }}
                    validationSchema={validationSchema}
                    onSubmit={() => setOpenSummary(true)}
                >
                    {({ values, errors, touched, handleBlur, handleChange, setFieldValue, submitForm }) => (
                        <Form>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" >
                                <Header title="Student Registration" subtitle="Register a new student and link to a guardian" />
                                <Button 
                                    variant="contained" 
                                    onClick={submitForm}
                                    startIcon={<Save />} 
                                    sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 2, px: 4, fontWeight: 'bold', '&:hover': { bgcolor: colors.accentDark } }}
                                >
                                    Review & Save
                                </Button>
                            </Stack>

                            <Grid container spacing={3}>
                                {/* --- LEFT: GUARDIAN & ACADEMIC (md=4) --- */}
                                <Grid item xs={12} md={4}>
                                    <Stack spacing={3}>
                                        {/* Guardian Paper */}
                                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                                <FamilyRestroom sx={{ color: colors.accent }} />
                                                <Typography variant="subtitle1" fontWeight="bold">Guardian Selection</Typography>
                                            </Stack>
                                            <Autocomplete
                                                options={parentList}
                                                getOptionLabel={(option) => option.fullname}
                                                value={parentList.find(option => option.parentId === values.parentId) || null}
                                                onChange={(e, newValue) => setFieldValue("parentId", newValue ? newValue.parentId : "")}
                                                renderInput={(params) => (
                                                    <TextField 
                                                        {...params} 
                                                        label="Search Guardian Name"
                                                        size="small" 
                                                        error={touched.parentId && !!errors.parentId}
                                                        helperText={touched.parentId && errors.parentId}
                                                        sx={{ bgcolor: colors.inputBg }}
                                                    />
                                                )}
                                            />
                                        </Paper>

                                        {/* Enrollment Paper */}
                                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                                <School sx={{ color: colors.accent }} />
                                                <Typography variant="subtitle1" fontWeight="bold">Academic Details</Typography>
                                            </Stack>
                                            <Stack spacing={2}>
                                                <TextField select fullWidth label="Assign Batch" name="class" size="small" value={values.class}
                                                    onChange={(e) => { handleChange(e); setFieldValue("course", []); }}
                                                    error={touched.class && !!errors.class}
                                                    sx={{ bgcolor: colors.inputBg }}
                                                >
                                                    {classList.filter(val => val.status === "A").map((val) => (
                                                        <MenuItem key={val.batchId} value={val.batchId}>{val.batchName}</MenuItem>
                                                    ))}
                                                </TextField>

                                                <TextField label="Package Amount" name="amount" size="small" value={values.amount} onChange={handleChange}
                                                    InputProps={{ startAdornment: <InputAdornment position="start">RM</InputAdornment> }}
                                                    error={touched.amount && !!errors.amount}
                                                    sx={{ bgcolor: colors.inputBg }}
                                                />
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                </Grid>

                                {/* --- RIGHT: STUDENT DETAILS (md=8) --- */}
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: `1px solid ${colors.border}` }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Person sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                                        </Stack>
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField fullWidth label="Student Full Name" name="fullname" value={values.fullname} onChange={handleChange} 
                                                    size="small" sx={{ bgcolor: 'white' }} required
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField select fullWidth label="Gender" name="gender" value={values.gender} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }}>
                                                    <MenuItem value="Lelaki">Male</MenuItem>
                                                    <MenuItem value="Perempuan">Female</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField type="date" fullWidth label="Date of Birth" name="dateOfBirth" value={values.dateOfBirth} onChange={handleChange} 
                                                    size="small" InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Course Enrollment</Typography>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Select Enrolled Courses</InputLabel>
                                                    <Select
                                                        multiple
                                                        name="course"
                                                        label="Select Enrolled Courses"
                                                        value={values.course}
                                                        onChange={handleChange}
                                                        sx={{ bgcolor: 'white' }}
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((val) => (
                                                                    <Chip key={val} label={courseList.find(c => c.courseId === val)?.courseName} size="small" />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    >
                                                        {values.class ? 
                                                            courseList.filter(c => c.class === classList.find(b => b.batchId === values.class)?.batchName).map(c => (
                                                                <MenuItem key={c.courseId} value={c.courseId}>{c.courseName}</MenuItem>
                                                            )) 
                                                            : <MenuItem disabled>Select a Batch first to see available courses</MenuItem>
                                                        }
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Confirm Dialog */}
                            <Dialog fullWidth maxWidth="sm" open={openSummary} onClose={() => setOpenSummary(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: colors.tableHeader }}>Confirm Registration</DialogTitle>
                                <DialogContent sx={{ mt: 2 }}>
                                    <Stack spacing={2}>
                                        <Box display="flex" justifyContent="space-between"><Typography>Guardian:</Typography><Typography fontWeight="bold">{parentList.find(p => p.parentId === values.parentId)?.fullname}</Typography></Box>
                                        <Box display="flex" justifyContent="space-between"><Typography>Student Name:</Typography><Typography fontWeight="bold">{values.fullname}</Typography></Box>
                                        <Box display="flex" justifyContent="space-between"><Typography>Batch:</Typography><Typography fontWeight="bold">{classList.find(c => c.batchId === values.class)?.batchName}</Typography></Box>
                                        <Divider />
                                        <Box display="flex" justifyContent="space-between"><Typography>Total Fee:</Typography><Typography fontWeight="bold" color="success.main">RM {values.amount}</Typography></Box>
                                    </Stack>
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}>
                                    <Button onClick={() => setOpenSummary(false)} color="inherit">Edit</Button>
                                    <Button onClick={() => CreateStudent(values)} variant="contained" disabled={loadingSubmit} sx={{ bgcolor: colors.accent, color: 'black', fontWeight: 'bold' }}>
                                        {loadingSubmit ? <CircularProgress size={20} /> : "Confirm Registration"}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Form>
                    )}
                </Formik>

                <Snackbar open={successCreateStudent} autoHideDuration={4000} onClose={() => setSuccessCreateStudent(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>Student created successfully!</Alert>
                </Snackbar>
                <Snackbar open={failCreateStudent} autoHideDuration={4000} onClose={() => setFailCreateStudent(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>{failErrorMsg || "Failed to create student."}</Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default AddStudentScreen;